import { useState } from 'react'
import { fbt } from 'fbtee'
import { Button } from '../../../components/ui/button'
import { ScheduleLeadDialog } from './ScheduleLeadDialog'
import { useAuth } from '../../../hooks/useAuth'
import { useGuestGuard } from '../../../hooks/useGuestGuard'
import { useScheduleLeads } from '../../../hooks/useScheduleLeads'
import { scheduleLeadPrompt } from '../../../lib/guestPrompts'

/**
 * The schedule empty state's way out. Nothing in the seeding pipeline looks back
 * at an event whose times weren't published when we seeded it, so the person
 * staring at the empty timetable is the best signal we get that they now are.
 *
 * Both emptinesses get the offer: a whole event with no schedule, and a single
 * bare day on an event that has one — promoters routinely publish a festival
 * day at a time, so a missing day is the same "not out yet" signal.
 *
 * Renders as the `message` node of ScheduleSkeleton, which wraps it in a <p> —
 * hence spans rather than divs.
 */
export function EmptyScheduleMessage({
  eventId,
  emptyDay = false,
}: {
  eventId: string
  /** The event has a schedule; this particular day is the bare one. */
  emptyDay?: boolean
}) {
  const [open, setOpen] = useState(false)
  const { signedInUid } = useAuth()
  const { guard } = useGuestGuard()
  const { submitLead, alreadyReported } = useScheduleLeads(eventId, signedInUid)
  const openReport = guard(() => setOpen(true), scheduleLeadPrompt())

  return (
    <>
      {emptyDay ? (
        <fbt desc="Empty state shown on the Schedule tab when the selected day has no sets">
          Nothing scheduled on this day yet.
        </fbt>
      ) : (
        <fbt desc="Empty state shown on the Schedule tab when an event has no sets">
          Schedule isn't available yet.
        </fbt>
      )}
      <span className="mt-2 block">
        {alreadyReported ? (
          <span className="text-xs" data-testid="schedule-lead-sent">
            <fbt desc="Confirmation shown to a user who already told us an event's set times are out">
              Thanks — we're on it.
            </fbt>
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            data-testid="schedule-lead-open"
            onClick={() => openReport()}
          >
            {fbt(
              'Set times are out?',
              'Button on the empty schedule for telling us set times are published',
            )}
          </Button>
        )}
      </span>

      <ScheduleLeadDialog open={open} onOpenChange={setOpen} onSubmit={submitLead} />
    </>
  )
}
